import axiosInstance from '../../api/axios';

// Types mirror skeptics-backend evidence_library_handlers.go / models.EvidenceLibraryFile JSON tags verbatim.

export type EvidenceRole = 'population' | 'ipe_proof' | 'evidence' | 'supporting';
export type LibraryStatus = 'unclassified' | 'classified' | 'used';
export type BatchScope = 'library' | 'control' | 'run';

export type EvidenceLibraryLimits = {
  max_file_bytes: number;
  max_files_per_upload: number;
  max_bytes_per_upload: number;
  max_folder_depth: number;
  max_files_per_request: number;
  allowed_extensions: string[];
  population_extensions: string[];
};

export type EvidenceLibraryFile = {
  id: number;
  filename: string;
  relative_path: string | null;
  content_type: string;
  file_size: number;
  audit_year_id: string | null;
  upload_batch_id: string | null;
  source: string | null;
  content_sha256: string | null;
  uploaded_at: string | null;
  link_count: number;
  used: boolean;
  status: LibraryStatus;
};

export type FileSuggestion = {
  file_id: number;
  score: number;
  suggested: boolean;
  control_match: 'high' | 'medium' | 'none';
  suggested_role: EvidenceRole;
  suggested_samples: string[];
  reasons: string[];
};

export type SuggestedLibraryFile = EvidenceLibraryFile & { suggestion: FileSuggestion };

export type LibrarySuggestions = {
  control_uuid: string;
  suggested: SuggestedLibraryFile[];
  /** Suggested role for every ranked file, keyed by file id. */
  roles: Record<string, EvidenceRole>;
  total_files: number;
  truncated: boolean;
};

export type LibraryListParams = {
  audit_year_id?: string | null;
  status?: LibraryStatus | '';
  file_category?: EvidenceRole | '';
  sox_control_id?: string;
  batch_id?: string;
  q?: string;
  page?: number;
  page_size?: number;
};

export type LibraryListResponse = { files: EvidenceLibraryFile[]; total: number; page: number };

export type SuggestionParams = {
  sox_control_id?: string;
  test_run_id?: string;
  audit_year_id?: string | null;
  period_start?: string;
  period_end?: string;
};

export type EvidenceUploadBatch = {
  id: string;
  audit_year_id: string | null;
  scope: BatchScope;
  sox_control_id: string | null;
  test_run_id: string | null;
  file_category: EvidenceRole | null;
  files_expected: number;
  files_uploaded: number;
  files_duplicate: number;
  files_skipped: number;
  files_failed: number;
  bytes_uploaded: number;
  status: 'open' | 'completed';
};

export type CreateBatchBody = {
  audit_year_id?: string | null;
  scope: BatchScope;
  sox_control_id?: string;
  test_run_id?: string;
  file_category?: EvidenceRole;
  testing_period_start?: string;
  testing_period_end?: string;
  label?: string;
  files_expected: number;
  bytes_expected: number;
};

export type LibraryFileMeta = {
  client_id: string;
  filename: string;
  relative_path: string;
  size: number;
  content_type: string;
  sha256: string;
  object_key?: string;
};

export type InitFileResult = {
  client_id: string;
  status: 'upload' | 'duplicate' | 'rejected';
  reason?: string;
  upload_url?: string;
  object_key?: string;
  content_type?: string;
  existing_upload_id?: number;
  evidence_id?: number;
  /** A duplicate the upload's control now uses (nothing new stored, but the file was added). */
  linked?: boolean;
};

export type CompleteFileResult = {
  client_id: string;
  status: 'uploaded' | 'duplicate' | 'failed';
  reason?: string;
  upload_id?: number;
  evidence_id?: number;
  linked?: boolean;
};

export type LinkItem = {
  control_upload_id: number;
  file_category: EvidenceRole;
  testing_period_start?: string;
  testing_period_end?: string;
  step_mapping?: unknown;
};

export type LinkResult = {
  control_upload_id: number;
  status: 'linked' | 'already_linked' | 'error';
  evidence_id?: number;
  file_category?: EvidenceRole;
  error?: string;
};

const BASE = '/api/evidence-library';

function clean<T extends Record<string, unknown>>(params: T): Partial<T> {
  const out: Partial<T> = {};
  (Object.keys(params) as (keyof T)[]).forEach((k) => {
    const v = params[k];
    if (v !== undefined && v !== null && v !== '') out[k] = v;
  });
  return out;
}

export async function fetchLibraryLimits(): Promise<EvidenceLibraryLimits> {
  const { data } = await axiosInstance.get(`${BASE}/limits`);
  return data;
}

export async function fetchLibraryFiles(params: LibraryListParams): Promise<LibraryListResponse> {
  const { data } = await axiosInstance.get(BASE, { params: clean(params) });
  return { files: data?.files ?? [], total: data?.total ?? 0, page: data?.page ?? 1 };
}

export async function fetchLibrarySuggestions(params: SuggestionParams): Promise<LibrarySuggestions> {
  const { data } = await axiosInstance.get(`${BASE}/suggestions`, { params: clean(params) });
  return { ...data, suggested: data?.suggested ?? [], roles: data?.roles ?? {} };
}

export async function createUploadBatch(body: CreateBatchBody): Promise<EvidenceUploadBatch> {
  const { data } = await axiosInstance.post(`${BASE}/batches`, clean(body));
  return data.batch;
}

export async function initBatchFiles(batchId: string, files: LibraryFileMeta[]): Promise<InitFileResult[]> {
  const { data } = await axiosInstance.post(`${BASE}/batches/${batchId}/files/init`, { files });
  return data?.files ?? [];
}

export async function completeBatchFiles(batchId: string, files: LibraryFileMeta[]): Promise<CompleteFileResult[]> {
  const { data } = await axiosInstance.post(`${BASE}/batches/${batchId}/files/complete`, { files });
  return data?.files ?? [];
}

export async function finishUploadBatch(batchId: string, filesFailed: number): Promise<EvidenceUploadBatch> {
  const { data } = await axiosInstance.post(`${BASE}/batches/${batchId}/finish`, { files_failed: filesFailed });
  return data.batch;
}

export async function linkLibraryFiles(body: { sox_control_id: string; test_run_id?: string; items: LinkItem[] }): Promise<LinkResult[]> {
  const { data } = await axiosInstance.post(`${BASE}/link`, body);
  return data?.results ?? [];
}

/** Parse a library file as a population (same response as /api/test-runs/population/parse). */
export async function parseLibraryPopulation(fileId: number, identifierColumn?: string | null): Promise<any> {
  const { data } = await axiosInstance.post(`${BASE}/files/${fileId}/parse-population`,
    identifierColumn ? { identifier_column: identifierColumn } : {});
  return data;
}

export type BulkRemoveStatus = 'unlinked' | 'deleted' | 'deleted_retained' | 'kept_shared' | 'blocked' | 'error';

export type BulkRemoveResult = {
  control_upload_id: number;
  filename: string;
  evidence_ids: number[];
  status: BulkRemoveStatus;
  detail?: string;
  other_controls?: string[];
};

/** Unlink evidence from a control and, optionally, remove the files from the library (files runs used are kept for them). */
export async function bulkRemoveControlEvidence(controlUuid: string, evidenceIds: number[], deleteFromLibrary: boolean): Promise<BulkRemoveResult[]> {
  const { data } = await axiosInstance.post(`/api/sox-controls/${controlUuid}/evidence/bulk-remove`, {
    evidence_ids: evidenceIds,
    delete_from_library: deleteFromLibrary,
  });
  return data?.results ?? [];
}
